import React from "react";
import { Megaphone, X, RefreshCw } from "lucide-react";
import type { WhatsAppCampaign } from "../../../../Data/whatsapp.dto";
import {
  ModalBackdrop,
  ModalBoxLarge,
  ModalHeaderRow,
  ModalHeading,
  ModalSubHeading,
  CloseModalBtn,
  LoadingDetailWrap,
  DetailModalBody,
  DetailSummaryGrid,
  SummaryCard,
  SummaryLabel,
  SummaryValue,
  SummarySub,
  RecipientsTableWrap,
  TableTitle,
  NoRecipientsBox,
  RecipientsTable,
  StatusBadge,
} from "./CampaignManagerStyles";

export interface CampaignDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: WhatsAppCampaign | null;
  loading: boolean;
  formatCurrency: (amount?: string | number) => string;
}

const formatRecipientPhone = (r: any): string => {
  if (!r) return "—";
  const raw =
    r.customer_number ||
    r.phone_number ||
    r.mobile_number ||
    r.phone ||
    r.mobile ||
    r.number ||
    r.integrated_number ||
    r.to ||
    r.user?.customer_number ||
    r.user?.phone_number ||
    r.user?.phone ||
    r.user?.mobile_number ||
    "";
  if (!raw) return "—";
  const str = String(raw).trim();
  if (r.country_code && !str.startsWith("+") && !str.startsWith(String(r.country_code))) {
    const code = String(r.country_code).startsWith("+") ? r.country_code : `+${r.country_code}`;
    return `${code} ${str}`;
  }
  if (!str.startsWith("+")) {
    if (str.length === 12 && str.startsWith("91")) {
      return `+91 ${str.slice(2)}`;
    }
    if (str.length === 10) {
      return `+91 ${str}`;
    }
    return `+${str}`;
  }
  return str;
};

const formatRecipientName = (r: any): string => {
  if (!r) return "Client";
  return (
    r.customer_name ||
    r.name ||
    r.client_name ||
    r.user_name ||
    r.username ||
    r.user?.customer_name ||
    r.user?.name ||
    r.user?.full_name ||
    "Client"
  );
};

export const CampaignDetailModal: React.FC<CampaignDetailModalProps> = ({
  isOpen,
  onClose,
  campaign,
  loading,
  formatCurrency,
}) => {
  if (!isOpen) return null;

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalBoxLarge onClick={(e) => e.stopPropagation()}>
        <ModalHeaderRow>
          <Megaphone size={20} color="#c94daa" />
          <div>
            <ModalHeading>
              {campaign?.name || "Campaign Details & Results"}
            </ModalHeading>
            <ModalSubHeading>
              {campaign?.template_name || "Template"} · Status:{" "}
              <strong>{(campaign?.status || "loading").toUpperCase()}</strong>
            </ModalSubHeading>
          </div>
          <CloseModalBtn onClick={onClose}>
            <X size={18} />
          </CloseModalBtn>
        </ModalHeaderRow>

        {loading ? (
          <LoadingDetailWrap>
            <RefreshCw size={24} className="spin-animation" color="#c94daa" />
            <span>Loading recipient logs and wallet breakdown...</span>
          </LoadingDetailWrap>
        ) : campaign ? (
          <DetailModalBody>
            {/* Financial & Delivery Summary */}
            <DetailSummaryGrid>
              <SummaryCard>
                <SummaryLabel>Delivery Success</SummaryLabel>
                <SummaryValue style={{ color: "#16a34a" }}>
                  {campaign.sent_count ?? 0} Sent
                </SummaryValue>
                <SummarySub>
                  {campaign.failed_count ?? 0} Failed
                </SummarySub>
              </SummaryCard>

              <SummaryCard>
                <SummaryLabel>Wallet Debited</SummaryLabel>
                <SummaryValue style={{ color: "#c94daa" }}>
                  {formatCurrency(campaign.wallet_charged_inr)}
                </SummaryValue>
                <SummarySub>
                  Category: {(campaign.pricing_category || "marketing").toUpperCase()}
                </SummarySub>
              </SummaryCard>

              <SummaryCard>
                <SummaryLabel>Recipients Billed</SummaryLabel>
                <SummaryValue>
                  {campaign.wallet_charged_count ?? campaign.sent_count ?? 0}
                </SummaryValue>
                <SummarySub>One debit per client conversation</SummarySub>
              </SummaryCard>
            </DetailSummaryGrid>

            {/* Recipients Table */}
            <RecipientsTableWrap>
              <TableTitle>Recipients Breakdown ({campaign.recipients?.length ?? 0})</TableTitle>
              {!campaign.recipients || campaign.recipients.length === 0 ? (
                <NoRecipientsBox>
                  No recipient logs available for this campaign.
                </NoRecipientsBox>
              ) : (
                <RecipientsTable>
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Charge</th>
                      <th>Resolved Text / Variables</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaign.recipients.map((r, i) => (
                      <tr key={r.id || i}>
                        <td style={{ fontWeight: 700 }}>
                          {formatRecipientName(r)}
                        </td>
                        <td>{formatRecipientPhone(r)}</td>
                        <td>
                          <StatusBadge $status={r.status || "sent"}>
                            {r.status || "Sent"}
                          </StatusBadge>
                        </td>
                        <td>{formatCurrency(r.charge_inr || "1.00")}</td>
                        <td>
                          {r.error_message ? (
                            <span style={{ color: "#dc2626", fontSize: 12 }}>
                              {r.error_message}
                            </span>
                          ) : r.rendered_text ? (
                            <span style={{ fontSize: 12, color: "#374151", whiteSpace: "pre-line" }}>
                              {r.rendered_text}
                            </span>
                          ) : r.resolved_variables ? (
                            <span style={{ fontSize: 12, color: "#4b5563" }}>
                              {Array.isArray(r.resolved_variables)
                                ? r.resolved_variables.join(", ")
                                : JSON.stringify(r.resolved_variables)}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </RecipientsTable>
              )}
            </RecipientsTableWrap>
          </DetailModalBody>
        ) : (
          <NoRecipientsBox>Campaign information could not be loaded.</NoRecipientsBox>
        )}
      </ModalBoxLarge>
    </ModalBackdrop>
  );
};

export default CampaignDetailModal;
